/**
 * Entries saved while the phone has no signal wait here and are sent later. Each carries a
 * clientRequestId, so a resend never creates a second entry on the server.
 */
export type QueuedEntry = {
  clientRequestId: string;
  body: Record<string, unknown>;
  loomCode: string;
  queuedAt: string;
  error?: string;
};

type Storage = Pick<globalThis.Storage, "getItem" | "setItem">;

const key = (orgId: string) => `hkp.karkhana.entryQueue.${orgId}`;

export const newRequestId = (): string => {
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const readQueue = (orgId: string, store: Storage = localStorage): QueuedEntry[] => {
  try {
    const v = JSON.parse(store.getItem(key(orgId)) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

const write = (orgId: string, q: QueuedEntry[], store: Storage) => {
  try {
    store.setItem(key(orgId), JSON.stringify(q));
  } catch {
    // Storage full or blocked: the entry stays on screen with an error instead.
  }
};

export const enqueue = (orgId: string, item: QueuedEntry, store: Storage = localStorage) => {
  const q = readQueue(orgId, store).filter((x) => x.clientRequestId !== item.clientRequestId);
  write(orgId, [...q, item], store);
};

/**
 * Send queued entries in order. Stops at the first network failure (still offline). An entry the
 * server rejects stays in the queue with its error so the supervisor can see and fix it.
 */
export const flushQueue = async (
  orgId: string,
  send: (body: Record<string, unknown>) => Promise<any>,
  store: Storage = localStorage
): Promise<{ sent: number; failed: number; offline: boolean }> => {
  let sent = 0;
  let failed = 0;
  const q = readQueue(orgId, store);
  const keep: QueuedEntry[] = [];
  for (let i = 0; i < q.length; i++) {
    const item = q[i];
    let res: any;
    try {
      res = await send({ ...item.body, clientRequestId: item.clientRequestId });
    } catch {
      write(orgId, [...keep, ...q.slice(i)], store);
      return { sent, failed, offline: true };
    }
    if (res?.success) {
      sent++;
    } else {
      failed++;
      keep.push({ ...item, error: res?.message || "Not saved" });
    }
  }
  write(orgId, keep, store);
  return { sent, failed, offline: false };
};

export const dropFromQueue = (orgId: string, clientRequestId: string, store: Storage = localStorage) =>
  write(orgId, readQueue(orgId, store).filter((x) => x.clientRequestId !== clientRequestId), store);
